import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import ReactTableWrapper from "../../components/ReactTableWrapper";
import ListFormPageHeader from "../../components/ListFormPageHeader";
import ListPageGradientSummary from "../../components/ListPageGradientSummary";
import { exportAsCSV } from "../../utils/CSVExporter";
import { exportAsPDF } from "../../utils/PDFExporter";
import { useNavigate } from "react-router-dom";
import { Box } from '@mui/material';
import "../../App.css";
import Sidebar from '../../Sidebar';
import Swal from "sweetalert2";
import { useCookies } from 'react-cookie';
import { displayError } from "../../helpers";
import { openPdfResponse } from "../../utils/openPdfInApp";
import moment from 'moment';
import { getAllDeliveryChallans, deleteDeliveryChallan ,printBatchReport,printDeliveryChallan,getDcListCSV, reactivateDeliveryChallan } from "../../services/DeliveryChallanServices";
import { getListViewSettings } from "../../services/SettingsServices";
import { buildDeliveryChallanQueryParams, formatResolvedRange } from "../../utils/listViewDateRanges";
import { DELIVERY_CHALLAN_CREATE_DRAFT_KEY, navigateToNewForm } from "../../utils/formDraft";
import { getDefaultListViewConfig } from "../../config/listViewSettingsConfig";
import { isInactiveRecord } from "../../utils/listSummaryUtils";
import { getHTMLForTablePopup } from "../../utils/PopupUtils";

function capitalizeLabel(value) {
    const text = String(value || '').trim();
    if (!text) return '';
    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
}

function DeliveryChallanTable({ viewOnly: viewOnlyProp = false }) {
    const InitialData = {
        total_pages: 0,
        records: [],
      };
    const [data, setData] = useState(InitialData);
    const navigate = useNavigate(); 
    const [refreshKey, setRefreshKey] = useState(0);
    const [cookies] = useCookies(['myToken']);
    // Bharat Concrete: start false so the table never opens stuck on skeletons.
    const [isLoading, setIsLoading] = useState(false);  
    const [dates,setDates]=useState({}) 
    const left_pinned = ["delivery_challan_no","delivery_challan_date"]
    const right_pinned = ["id"]
    const [listViewConfig, setListViewConfig] = useState(null);
    const requestIdRef = useRef(0);
    const hasRowsRef = useRef(false);
    const allowedPlantIdsRef = useRef([]);
    const [filterMeta, setFilterMeta] = useState({ allowedPlantCount: 0 });

    const sumQty = (records, field) =>
        records.reduce((sum, row) => sum + (Number(row[field]) || 0), 0);

    const activeFilterLabel = useMemo(() => {
        if (!listViewConfig) return '';
        const range = formatResolvedRange(listViewConfig, 'delivery_challan');
        const plantIds = listViewConfig.plant_ids || [];
        const allowedCount = filterMeta.allowedPlantCount;
        const allPlantsSelected =
            !plantIds.length ||
            (allowedCount > 0 && plantIds.length >= allowedCount);
        const plantLabel = allPlantsSelected
            ? 'All plants'
            : `${plantIds.length} plant${plantIds.length === 1 ? '' : 's'}`;
        const statuses = listViewConfig.record_statuses || [];
        const statusLabel =
            statuses.length === 0 || (statuses.includes('true') && statuses.includes('false'))
                ? 'Active + Cancelled'
                : statuses.includes('true')
                    ? 'Active only'
                    : 'Cancelled only';
        return `${range} · ${plantLabel} · ${statusLabel} · ${data.records.length} row${data.records.length === 1 ? '' : 's'}`;
    }, [listViewConfig, filterMeta.allowedPlantCount, data.records.length]);

    const configForQuery = useCallback((config) => {
        const plantIds = config?.plant_ids || [];
        const allowed = allowedPlantIdsRef.current;
        const allPlantsSelected =
            !plantIds.length ||
            (allowed.length > 0 && plantIds.length >= allowed.length);
        return {
            ...config,
            // Empty plant_ids => backend returns all plants in user scope (faster / clearer).
            plant_ids: allPlantsSelected ? [] : plantIds,
        };
    }, []);

    const todayDateLabel = moment().format('DD-MM-YYYY');

    const qtyTotals = useMemo(() => {
        const activeRecords = data.records.filter((row) => !isInactiveRecord(row));
        const cancelledRecords = data.records.filter((row) => isInactiveRecord(row));
        const todayActiveRecords = activeRecords.filter(
            (row) => row.delivery_challan_date === todayDateLabel
        );
        return {
            // Active-only totals (cancelled rows are shown in list color but counted separately)
            production_qty: sumQty(activeRecords, 'production_qty'),
            dc_qty: sumQty(activeRecords, 'dc_qty'),
            accepted_qty: sumQty(activeRecords, 'accepted_qty'),
            today_dc_qty: sumQty(todayActiveRecords, 'dc_qty'),
            cancelled_qty: sumQty(cancelledRecords, 'dc_qty'),
            cancelled_count: cancelledRecords.length,
        };
    }, [data.records, todayDateLabel]);

    const formatQty = (value) => Number(value || 0).toFixed(2);

    const todayDcCard = useMemo(() => ({
        label: 'Today DC',
        value: formatQty(qtyTotals.today_dc_qty),
        gradient: 'linear-gradient(135deg, #b45309 0%, #f59e0b 55%, #fbbf24 100%)',
        shadow: '0 8px 20px rgba(245, 158, 11, 0.28)',
    }), [qtyTotals.today_dc_qty]);

    const summaryCards = useMemo(() => ([
        {
            label: 'Production',
            value: formatQty(qtyTotals.production_qty),
            gradient: 'linear-gradient(135deg, #1e3a8a 0%, #2563eb 55%, #60a5fa 100%)',
            shadow: '0 8px 20px rgba(37, 99, 235, 0.24)',
        },
        {
            label: 'DC Qty',
            value: formatQty(qtyTotals.dc_qty),
            gradient: 'linear-gradient(135deg, #4c1d95 0%, #7c3aed 55%, #a78bfa 100%)',
            shadow: '0 8px 20px rgba(124, 58, 237, 0.24)',
        },
        {
            label: 'Accepted',
            value: formatQty(qtyTotals.accepted_qty),
            gradient: 'linear-gradient(135deg, #0f766e 0%, #0d9488 55%, #2dd4bf 100%)',
            shadow: '0 8px 20px rgba(13, 148, 136, 0.24)',
        },
        {
            label: 'Cancelled',
            value: formatQty(qtyTotals.cancelled_qty),
            subValue: `${qtyTotals.cancelled_count} DC cancelled`,
            gradient: 'linear-gradient(135deg, #991b1b 0%, #dc2626 55%, #f87171 100%)',
            shadow: '0 8px 20px rgba(220, 38, 38, 0.28)',
        },
    ]), [qtyTotals]);

    const onReactivateDeliveryChallan = useCallback((deliveryId, event) => {
        if (event?.preventDefault) {
            event.preventDefault();
        }
        Swal.fire({
            title: 'Reactivate this cancelled Delivery Challan?',
            text: 'It will become Active again and work-schedule balance will be recalculated.',
            showCancelButton: true,
            confirmButtonText: 'Yes, Reactivate',
            cancelButtonText: 'No',
        }).then((result) => {
            if (!result.isConfirmed) {
                Swal.fire('Not Reactivated', '', 'info');
                return;
            }
            reactivateDeliveryChallan(cookies, deliveryId)
                .then(() => {
                    Swal.fire('Reactivated Successfully!', '', 'success');
                    setRefreshKey((oldKey) => oldKey + 1);
                })
                .catch((error) => {
                    displayError(error.response?.data || error.message, 'Reactivate Failed');
                });
        });
    }, [cookies]);

    const showCancelledDcDetails = useCallback(() => {
        const cancelledRows = data.records.filter((row) => isInactiveRecord(row));
        if (!cancelledRows.length) {
            Swal.fire('Cancelled DCs', 'No cancelled delivery challans in the current list.', 'info');
            return;
        }
        const totalQty = cancelledRows.reduce((sum, row) => sum + (Number(row.dc_qty) || 0), 0);
        const canReactivate = !viewOnlyProp;
        const columns = [
            { key: 'delivery_challan_no', label: 'DC No', align: 'center' },
            { key: 'delivery_challan_date', label: 'DC Date', align: 'center' },
            { key: 'plant', label: 'Plant', align: 'left' },
            { key: 'customer_name', label: 'Customer', align: 'left' },
            { key: 'consignee_projectname', label: 'Site', align: 'left' },
            { key: 'grade', label: 'Grade', align: 'left' },
            { key: 'customer_vehicle_no', label: 'Vehicle', align: 'left' },
            { key: 'dc_qty', label: 'DC Qty', align: 'right' },
        ];
        if (canReactivate) {
            columns.push({ key: 'action', label: 'Action', align: 'center' });
        }
        const html = getHTMLForTablePopup(
            `Cancelled Delivery Challans (${cancelledRows.length})`,
            columns,
            cancelledRows.map((row) => ({
                delivery_challan_no: row.delivery_challan_no,
                delivery_challan_date: row.delivery_challan_date,
                plant: row.plant,
                customer_name: row.customer_name,
                consignee_projectname: row.consignee_projectname,
                grade: row.grade,
                customer_vehicle_no: row.customer_vehicle_no,
                dc_qty: formatQty(row.dc_qty),
                action: canReactivate
                    ? `<button type="button" class="swal2-confirm swal2-styled dc-reactivate-btn" data-dc-id="${row.id}" style="padding:4px 10px;font-size:12px;margin:0;">Reactivate</button>`
                    : '',
            })),
            {
                delivery_challan_no: 'Total',
                delivery_challan_date: '',
                plant: '',
                customer_name: '',
                consignee_projectname: '',
                grade: '',
                customer_vehicle_no: `${cancelledRows.length} DC`,
                dc_qty: formatQty(totalQty),
                action: '',
            },
        );
        Swal.fire({
            html,
            width: '1040px',
            confirmButtonText: 'Close',
            didOpen: () => {
                if (!canReactivate) return;
                const popup = Swal.getHtmlContainer();
                if (!popup) return;
                popup.querySelectorAll('.dc-reactivate-btn').forEach((btn) => {
                    btn.addEventListener('click', (event) => {
                        event.preventDefault();
                        const dcId = Number(btn.getAttribute('data-dc-id'));
                        if (!dcId) return;
                        Swal.close();
                        onReactivateDeliveryChallan(dcId);
                    });
                });
            },
        });
    }, [data.records, viewOnlyProp, onReactivateDeliveryChallan]);

    const handleSummaryCardClick = useCallback((card) => {
        if (card?.label === 'Cancelled') {
            showCancelledDcDetails();
        }
    }, [showCancelledDcDetails]);

    const token = cookies.myToken;

    const loadDeliveryChallanList = useCallback((config) => {
        if (!token) {
            setIsLoading(false);
            return Promise.resolve();
        }
        const activeConfig = config || getDefaultListViewConfig('delivery_challan');
        const requestId = ++requestIdRef.current;
        const keepRowsVisible = hasRowsRef.current;
        if (!keepRowsVisible) {
            setIsLoading(true);
        }
        return getAllDeliveryChallans({ myToken: token }, {
            ...buildDeliveryChallanQueryParams(configForQuery(activeConfig)),
            limit: 500,
        })
        .then(deliverychallanList => {
            if (requestId !== requestIdRef.current) return;
            const tableData = (deliverychallanList?.delivery_challan_list || [])
            .map(dc => {
                const salesOrder = dc.work_schedule?.sales_order_detail?.sales_order;
                const prefix = salesOrder?.prefix ?? '';
                const orderNo = salesOrder?.order_no ?? '';
                return {
                    plant: dc.work_schedule?.plant?.alias_name ?? '',
                    delivery_challan_no: dc.delivery_challan_no,
                    delivery_challan_date: dc.delivery_challan_date,
                    delivery_challan_time: dc.delivery_challan_time,
                    batch_no: dc.batch_no,
                    order_no: `${prefix}${orderNo}`,
                    workschedule_no: dc.work_schedule?.schedule_no ?? '',
                    customer_name: salesOrder?.consignee?.customer?.name ?? '',
                    sales_rep_name: salesOrder?.consignee?.customer?.salesrep?.name ?? '',
                    consignee_projectname: salesOrder?.consignee?.project_name ?? '',
                    production_plant: dc.production_plant?.name ?? '',
                    production_qty: dc.production_qty,
                    purchase_qty: dc.purchase_qty,
                    other_qty: dc.other_qty,
                    received_qty: dc.received_qty,
                    client_adjust_qty: dc.client_adjust_qty,
                    dc_qty: dc.dc_qty,
                    accepted_qty: dc.accepted_qty ?? 0,
                    invoice_qty: dc.invoice?.invoice_qty,
                    customer_vehicle_no: dc.vehicle?.name ? dc.vehicle.name : dc.customer_vehicle_no,
                    delivery_mode: capitalizeLabel(dc.work_schedule?.delivery_mode),
                    grade: dc.work_schedule?.sales_order_detail?.product?.name ?? '',
                    dc_invioce: dc.status === false ? "Cancelled" : dc.invoice?.invioce_no ? dc.invoice.invioce_no : "Pending",
                    status: dc.status ? "Active" : "Cancelled",
                    id: dc.id,
                    is_batch_report_needed: salesOrder?.company?.is_batch_report_needed ? "Yes" : "No",
                    hide_row_edit: (dc.work_schedule?.is_schedule_completed || dc.status === false) ? "Yes" : "No",
                    show_row_reactivate: dc.status === false ? "Yes" : "No",
                    hide_row_print: dc.checkout_no ? "No" : "Yes",
                };
            });

            hasRowsRef.current = tableData.length > 0;
            setData({ records: tableData });
            setIsLoading(false);
        })
        .catch((error) => {
            if (requestId !== requestIdRef.current) return;
            console.log(error.response?.data ?? error.message);
            if (!keepRowsVisible) {
                hasRowsRef.current = false;
                setData({ records: [] });
            }
            const errorData = error.response?.data ?? error.message;
            if (error.code === 'ECONNABORTED') {
                displayError('Delivery challan list timed out. Try a shorter date range in General Settings.', 'Request timed out');
            } else {
                displayError(errorData, 'Failed to load delivery challan list');
            }
            setIsLoading(false);
        });
    }, [token, configForQuery]);

    const handleSettingsSaved = useCallback((config) => {
        setListViewConfig(config);
        loadDeliveryChallanList(config);
    }, [loadDeliveryChallanList]);

    // Bharat Concrete pattern: resolve settings once, then a single list fetch.
    useEffect(() => {
        if (!token) {
            setIsLoading(false);
            return undefined;
        }
        let cancelled = false;
        const fallback = getDefaultListViewConfig('delivery_challan');
        getListViewSettings({ myToken: token }, 'delivery_challan')
            .then((response) => {
                if (cancelled) return null;
                const config = response?.config || fallback;
                const allowed = response?.allowed_plant_ids || [];
                allowedPlantIdsRef.current = allowed;
                setFilterMeta({ allowedPlantCount: allowed.length });
                setListViewConfig(config);
                return loadDeliveryChallanList(config);
            })
            .catch(() => {
                if (cancelled) return null;
                allowedPlantIdsRef.current = [];
                setFilterMeta({ allowedPlantCount: 0 });
                setListViewConfig(fallback);
                return loadDeliveryChallanList(fallback);
            });
        return () => {
            cancelled = true;
        };
    }, [token, loadDeliveryChallanList, refreshKey]);
    const columns = useMemo(
        () => [
            { fieldName: "delivery_challan_no", headerName: 'DC.No', size: 110 },
            { fieldName: "delivery_challan_date", headerName: 'DC Date', size: 110 },
            { fieldName: "plant", headerName: 'Plant', size: 140 },
            { fieldName: "delivery_challan_time", headerName: 'DC Time', size: 100 },
            { fieldName: "order_no", headerName: 'SO No', size: 160 },
            { fieldName: "workschedule_no", headerName: 'WS No', size: 110 },
            { fieldName: 'customer_name', headerName: 'Customer', size: 200 },
            { fieldName: 'sales_rep_name', headerName: 'Marketing', size: 140 },
            { fieldName: "consignee_projectname", headerName: 'Site/Project', size: 200 },
            { fieldName: 'production_plant', headerName: 'Prod. Plant', size: 140 },
            { fieldName: 'grade', headerName: 'Grade', size: 120 },
            { fieldName: "production_qty", headerName: 'Prod Qty', size: 90, align: 'right' },
            { fieldName: "purchase_qty", headerName: 'Pur Qty', size: 90, align: 'right' },
            { fieldName: "received_qty", headerName: 'Rec Qty', size: 90, align: 'right' },
            { fieldName: "other_qty", headerName: 'Oth Qty', size: 90, align: 'right' },
            { fieldName: "client_adjust_qty", headerName: 'Client Qty', size: 100, align: 'right' },
            { fieldName: "dc_qty", headerName: 'DC Qty', size: 90, align: 'right' },
            { fieldName: "accepted_qty", headerName: 'Acc Qty', size: 90, align: 'right' },
            { fieldName: "invoice_qty", headerName: 'Inv Qty', size: 90, align: 'right' },
            { fieldName: "customer_vehicle_no", headerName: 'Vehicle', size: 120 },
            { fieldName: "delivery_mode", headerName: 'Delivery', size: 110 },
            { fieldName: "status", headerName: 'Status', size: 100 },
            { fieldName: "dc_invioce", headerName: 'Inv Status', size: 110 },
            { fieldName: "id", headerName: "ROW_ACTIONS" },
        ],
        [],);

    const tableColumns = useMemo(
        () => (viewOnlyProp
            ? columns.filter((col) => col.headerName !== 'ROW_ACTIONS')
            : columns),
        [columns, viewOnlyProp],
    );

const onEditDeliveryChallan = (deliveryId, event) => {
    navigate(`/DeliveryChallanEdit/${deliveryId}/`);
};

const onDeleteDeliveryChallan = (deliveryId, event) => {
    event.preventDefault();
    Swal.fire({title: 'Are you sure to Delete?',  
    showCancelButton: true,  
    confirmButtonText: `Yes`,  
    cancelButtonText: `No`,
    }).then((result) => {  
        if (result.isConfirmed) { 

        deleteDeliveryChallan(cookies, deliveryId)
        .then(response => {
            console.log(`Delivery with id ${deliveryId} deleted`);
            Swal.fire('Deleted Successfully!', '', 'success');
            setRefreshKey(oldKey => oldKey +1)
        })
        .catch(error => {
            setRefreshKey(oldKey => oldKey +1);
            console.log(error.response.data);
            displayError(error.response.data,'Delete Failed');
        });
        } else if (result.isDismissed) {    
        Swal.fire('Not Deleted', '', 'info')  
    }
    });
	  
};

const handleDownloadCsv = () => {
    if (!listViewConfig) return;
    const params = buildDeliveryChallanQueryParams(configForQuery(listViewConfig));
    getDcListCSV(cookies, params)
        .then(response => {
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'delivery_challan_list.csv');
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
        })
        .catch((error) => {
            displayError(error.response?.data ?? error.message, 'CSV Download Failed');
        });
};

const onPrintBr = (deliveryId, event) => {
    event.preventDefault();
    printBatchReport(cookies, deliveryId)
    .then(response => {
        openPdfResponse(response, { title: 'Batch Report', downloadFileName: 'batch_report.pdf' });
    })
    .catch(async (error) => {
        console.log(error);
        let errorData = error.response?.data ?? error.message;
        // Blob error responses need to be read as text/JSON
        if (errorData instanceof Blob) {
            try {
                const text = await errorData.text();
                errorData = JSON.parse(text);
            } catch (e) {
                errorData = 'Batch report print failed.';
            }
        }
        displayError(errorData, 'BR print Failed');
    });  
};

const onPrintDeliveryChallan = (deliveryId, event) => {
    event.preventDefault();
    printDeliveryChallan(cookies, deliveryId)
    .then(response => {
        openPdfResponse(response, { title: 'Delivery Challan', downloadFileName: 'delivery_challan.pdf' });
    })
    .catch(error => {
        // setRefreshKey(oldKey => oldKey +1);
        console.log(error);
        displayError(error,'Print Failed');
    });  
};

const handleCSVExport = (event) => {
    console.log('CSV Export');
    exportAsCSV(
        columns
        .filter(col => col.headerName !== 'ROW_ACTIONS')
        .map(c => c.headerName),
        data.records.map(dc => {

            const csvRow = {
                plant:dc.plant,
                delivery_challan_no: dc.delivery_challan_no,
                delivery_challan_date:dc.delivery_challan_date,
                delivery_challan_time:dc.delivery_challan_time,
                batch_no:dc.batch_no,
                order_no:dc.order_no,
                workschedule_no:dc.workschedule_no,
                customer_name:dc.customer_name,
                consignee_projectname:dc.consignee_projectname,
                production_plant:dc.production_plant,
                production_qty:dc.production_qty,
                purchase_qty:dc.purchase_qty,
                other_qty:dc.other_qty,
                received_qty:dc.received_qty,
                client_adjust_qty:dc.client_adjust_qty,
                dc_qty:dc.dc_qty,
                accepted_qty:dc.accepted_qty,
                invoice_qty:dc.invoice_qty,
                customer_vehicle_no:dc.customer_vehicle_no,
                delivery_mode:dc.delivery_mode,
                grade:dc.grade,
                dc_invioce:dc.dc_invioce,
                status:dc.status? "Active":"Cancelled",
                // id: dc.id,    
            }
            return csvRow;
            }));
};

const handlePDFExport = (event) => {
    console.log('PDF Export');
    exportAsPDF(
        'List of Delivery Challan',
        columns
        .filter(col => col.headerName !== 'ROW_ACTIONS')
        .map(col=>({header: col.headerName, dataKey: col.fieldName })),
        data.records.map(dc => ({
            plant:dc.plant,
            delivery_challan_no: dc.delivery_challan_no,
            delivery_challan_date:dc.delivery_challan_date,
            delivery_challan_time:dc.delivery_challan_time,
            batch_no:dc.batch_no,
            order_no:dc.order_no,
            workschedule_no:dc.workschedule_no,
            customer_name:dc.customer_name,
            consignee_projectname:dc.consignee_projectname,
            production_plant:dc.production_plant,
            production_qty:dc.production_qty,
            purchase_qty:dc.purchase_qty,
            other_qty:dc.other_qty,
            received_qty:dc.received_qty,
            client_adjust_qty:dc.client_adjust_qty,
            dc_qty:dc.dc_qty,
            accepted_qty:dc.accepted_qty,
            invoice_qty:dc.invoice_qty,
            customer_vehicle_no:dc.customer_vehicle_no,
            delivery_mode:dc.delivery_mode,
            grade:dc.grade,
            dc_invioce:dc.dc_invioce,
            status:dc.status? "Active":"Cancelled",
            id: dc.id,    
        })),
        'DeliveryChallanList.pdf'
    );
};

return (

    <div id="outer-container"  className="App" > 
        <Sidebar pageWrapId={'page-wrap'} outerContainerId={'outer-container'} />
        <div id="page-wrap">
        <ListFormPageHeader
            module={viewOnlyProp ? "QUALITY CONTROL" : "DISPATCH"}
            title="List of Delivery Challan"
            subtitle={viewOnlyProp
                ? "View delivery challans for quality control. This list is read-only."
                : undefined}
            viewKey="delivery_challan"
            onSettingsSaved={handleSettingsSaved}
            formLink={viewOnlyProp ? undefined : "/DeliveryChallan"}
            onFormLinkClick={viewOnlyProp ? undefined : (() => {
                navigateToNewForm(navigate, '/DeliveryChallan', [
                    DELIVERY_CHALLAN_CREATE_DRAFT_KEY,
                ]);
            })}
            addNewLabel="New Delivery Challan"
            onExportCSV={viewOnlyProp ? undefined : handleDownloadCsv}
            onExportPDF={viewOnlyProp ? undefined : handlePDFExport}
        />

            {activeFilterLabel ? (
                <Box className="container" sx={{ mb: 1.25, color: '#334155', fontSize: '0.88rem' }}>
                    <strong>Active filters:</strong> {activeFilterLabel}
                </Box>
            ) : null}

            <ListPageGradientSummary
                highlightCard={todayDcCard}
                cards={summaryCards}
                onCardClick={handleSummaryCardClick}
            />

            <div className="container item-list-table-container">
                <div className="table-responsive">
                <ReactTableWrapper
                            title='List of DeliveryChallan'
                            columns={tableColumns}
                            data={data.records}
                            onRowEdit={viewOnlyProp ? undefined : onEditDeliveryChallan}
                            onRowDelete={viewOnlyProp ? undefined : onDeleteDeliveryChallan}
                            onCSVExport={viewOnlyProp ? undefined : handleDownloadCsv}
                            onPDFExport={viewOnlyProp ? undefined : handlePDFExport}
                            enableExportToolbar={!viewOnlyProp}
                            isLoading={isLoading && data.records.length === 0}
                            hideDelete={true}
                            onRowBrPrint={viewOnlyProp ? undefined : onPrintBr}
                            onRowPrint={viewOnlyProp ? undefined : onPrintDeliveryChallan}
                            onRowReactivate={viewOnlyProp ? undefined : onReactivateDeliveryChallan}
                            hidePrint={viewOnlyProp}
                            left_pinned={viewOnlyProp ? undefined : left_pinned}
                            right_pinned={viewOnlyProp ? undefined : right_pinned}
                            compactColumns={true}
                        />
                </div>
            </div> 
        </div>
    </div>
);

}



export default DeliveryChallanTable;